/**
 * AdminTeachers — bucket 3 of 6, matching `admin-teachers.jsx`.
 * KPI strip over a 1.6fr/1fr grid:
 *
 *   left:  Regional Staffing Roster — one row per region (Director,
 *          Instructors, Assistants, Issues, Open shifts, Coverage bar).
 *   right: Next Payday · Hiring Pipeline · Commission Payouts — three
 *          mocked skeletons (no data source) behind MockedOverlay (BON-156).
 *
 * Range tabs (Week / Pay period / Month / Quarter) are decorative local
 * state — the backend does not yet accept a time-range param (BON-156).
 */
import { ChevronDown, Filter, MapPin, Plus } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, SegmentedControl } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';

import { useAdminTeachers, type AdminRegionRow, type AdminTeachersRegionRow } from './adminApi';
import { AdminTeachersMockedRail } from './AdminTeachersMockedRail';

type Range = 'Week' | 'Pay period' | 'Month' | 'Quarter';

const RANGE_OPTIONS: ReadonlyArray<{ value: Range; label: string }> = [
  { value: 'Week', label: 'Week' },
  { value: 'Pay period', label: 'Pay period' },
  { value: 'Month', label: 'Month' },
  { value: 'Quarter', label: 'Quarter' },
];

function regionCode(slug: string): string {
  const initials = slug
    .split('-')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  if (initials.length >= 2) return initials.slice(0, 2);
  return slug.slice(0, 2).toUpperCase();
}

function coverageTone(pct: number): string {
  if (pct < 80) return 'var(--nb-warn)';
  if (pct < 90) return 'var(--nb-info)';
  return 'var(--nb-primary)';
}

function issueTone(issues: number): string {
  if (issues >= 6) return 'var(--nb-warn)';
  if (issues >= 4) return 'var(--nb-info)';
  return 'var(--nb-primary)';
}

/** A column's reporting window, under its header. The roster mixes a
 *  trailing-6-month look-back (shifts worked) with a next-6-week look-ahead
 *  (issues, open shifts, coverage), so each column states which it is. */
function WindowNote({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontWeight: 400,
        fontSize: 9.5,
        letterSpacing: '.02em',
        color: 'var(--nb-subtle)',
        textTransform: 'none',
      }}
    >
      {children}
    </div>
  );
}

function RosterRow({ row }: { row: AdminTeachersRegionRow }) {
  const covTone = coverageTone(row.coveragePct);
  return (
    <tr>
      <td style={{ paddingLeft: 18 }}>
        <div style={{ fontWeight: 700 }}>{row.region.name}</div>
        <div className="nb-mono" style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>
          {regionCode(row.region.slug)}
        </div>
      </td>
      <td className="nb-muted">{row.director ?? '—'}</td>
      <td className="nb-tabular nb-mono">{row.teacherShifts}</td>
      <td className="nb-tabular nb-mono">{row.assistantShifts}</td>
      <td
        className="nb-tabular nb-mono"
        style={{
          color: issueTone(row.staffingIssues),
          fontWeight: row.staffingIssues > 3 ? 700 : 500,
        }}
      >
        {row.staffingIssues}
      </td>
      <td className="nb-tabular nb-mono">{row.openShifts}</td>
      <td style={{ paddingRight: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              flex: 1,
              height: 6,
              background: 'var(--nb-surface-2)',
              borderRadius: 999,
              overflow: 'hidden',
              minWidth: 60,
            }}
          >
            <div style={{ width: `${row.coveragePct}%`, height: '100%', background: covTone }} />
          </div>
          <span className="nb-tabular nb-mono" style={{ fontSize: 10.5, color: 'var(--nb-muted)' }}>
            {row.coveragePct}%
          </span>
        </div>
      </td>
    </tr>
  );
}

function RosterTable({ rows }: { rows: AdminTeachersRegionRow[] }) {
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--nb-subtle)', padding: '12px 18px' }}>
        No active regions.
      </div>
    );
  }
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <table className="nb-tbl">
        <thead>
          <tr>
            <th style={{ paddingLeft: 18 }}>Region</th>
            <th>Director</th>
            <th>
              Teacher Shifts
              <WindowNote>past 6 mo</WindowNote>
            </th>
            <th>
              Assistant Shifts
              <WindowNote>past 6 mo</WindowNote>
            </th>
            <th>
              Issues
              <WindowNote>next 6 wk</WindowNote>
            </th>
            <th>
              Open shifts
              <WindowNote>next 6 wk</WindowNote>
            </th>
            <th style={{ paddingRight: 18 }}>
              Coverage
              <WindowNote>next 6 wk</WindowNote>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <RosterRow key={row.region.id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegionScopePicker({ regions }: { regions: AdminRegionRow[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="nb-btn"
        style={{ fontSize: 11.5 }}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <MapPin size={13} />
        All regions ({regions.length})
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 100,
            background: 'var(--nb-bg)',
            border: '1px solid var(--nb-border)',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
            minWidth: 160,
            padding: '4px 0',
          }}
        >
          {regions.map((r) => (
            <div
              key={r.id}
              role="option"
              aria-selected={false}
              style={{ padding: '6px 14px', fontSize: 12, color: 'var(--nb-ink)' }}
            >
              {r.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminTeachers() {
  const [range, setRange] = useState<Range>('Week');
  const q = useAdminTeachers();

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  const { regionStaffing = [], kpis } = q.data.data;
  const totalOpenShifts = regionStaffing.reduce((s, r) => s + r.openShifts, 0);
  const totalIssues = regionStaffing.reduce((s, r) => s + r.staffingIssues, 0);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
        <div style={{ opacity: 0.4, pointerEvents: 'none' }}>
          <RegionScopePicker regions={q.data.scope.regions} />
        </div>
        <div style={{ opacity: 0.4, pointerEvents: 'none' }}>
          <SegmentedControl
            value={range}
            onChange={setRange}
            options={RANGE_OPTIONS}
            ariaLabel="Time range"
          />
        </div>
        <button className="nb-btn" style={{ opacity: 0.4 }} disabled>
          <Filter size={13} />
          Time off
        </button>
        <button className="nb-btn nb-btn-primary" style={{ opacity: 0.4 }} disabled>
          <Plus size={13} />
          Bulk hire
        </button>
      </div>

      <KpiStrip items={adaptKpis(kpis)} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
          gap: 14,
          flex: 1,
          minHeight: 0,
        }}
      >
        <section
          className="nb-card nb-card-tight"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px 8px' }}>
            <h3 className="nb-display" style={{ margin: 0, fontSize: 13, letterSpacing: '.04em' }}>
              Regional Staffing Roster
            </h3>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
              next 6 wk: {totalOpenShifts} open shifts ·{' '}
              <span style={{ color: 'var(--nb-warn)', fontWeight: 700 }}>{totalIssues} issues</span>
            </span>
          </div>
          <RosterTable rows={regionStaffing} />
        </section>

        <AdminTeachersMockedRail />
      </div>
    </>
  );
}
